// Team management + membership roster (/v1/teams). The role ladder is
// viewer < member < maintainer < owner; the server enforces who may add/change/
// remove whom, the UI just surfaces the {code,message} outcomes.

import { getJSON, sendJSON, request } from "./http.js";
import type { ErasureResult } from "./account.js";

const BASE = "/v1/teams";

/** Role is the membership ladder, ascending. */
export type Role = "viewer" | "member" | "maintainer" | "owner";

export const ROLES: Role[] = ["viewer", "member", "maintainer", "owner"];

export interface Team {
  id: string;
  name: string;
  displayName: string;
  personal: boolean;
  /** The caller's own role in this team. */
  role: Role;
}

export interface Member {
  userId: string;
  email: string;
  name: string;
  role: Role;
  created: string;
}

/** roleAtLeast reports whether `role` is at or above `min` on the ladder. */
export function roleAtLeast(role: Role, min: Role): boolean {
  return ROLES.indexOf(role) >= ROLES.indexOf(min);
}

/** listTeams returns the caller's teams (newest first) with the caller's role. */
export async function listTeams(): Promise<Team[]> {
  const { items } = await getJSON<{ items: Team[] }>(BASE);
  return items;
}

/** createTeam creates a team; the caller becomes its owner. */
export function createTeam(name: string): Promise<Team> {
  return sendJSON<Team>(BASE, "POST", { name });
}

/**
 * deleteTeam erases a non-personal team with all its workspaces (owner only).
 * `verification` is a step-up grant for `delete_team` bound to this team id.
 * The route exists only while the server has erasure on (erasureAvailable in
 * api/account.ts). The members lose access at once; the answer says whether
 * the rest was already confirmed gone or still runs in the background.
 */
export function deleteTeam(teamId: string, verification: string): Promise<ErasureResult> {
  return sendJSON<ErasureResult>(`${BASE}/${encodeURIComponent(teamId)}`, "DELETE", {
    verification,
  });
}

/** listMembers returns a team's roster (viewer+). */
export async function listMembers(teamId: string): Promise<Member[]> {
  const { items } = await getJSON<{ items: Member[] }>(
    `${BASE}/${encodeURIComponent(teamId)}/members`,
  );
  return items;
}

/** changeRole updates a member's role (needs the higher of old/new role). */
export function changeRole(teamId: string, userId: string, role: Role): Promise<Member> {
  return sendJSON<Member>(
    `${BASE}/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
    "PATCH",
    { role },
  );
}

/** removeMember removes a member (self-leave always allowed; else maintainer+). */
export async function removeMember(teamId: string, userId: string): Promise<void> {
  await request(
    `${BASE}/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

// ----- Team secrets (write-only; values are never returned) -----
//
// Team secrets are inherited by every workspace in the team, which is why
// writing one takes maintainer+ where a workspace secret takes only member+.
// A workspace secret of the same name shadows the inherited value.

export interface TeamSecret {
  name: string;
  /** Builds ignore the secret: the cluster reserves that name. */
  reserved?: boolean;
}

/** listTeamSecrets returns the team's secret NAMES only (viewer+), sorted. */
export async function listTeamSecrets(teamId: string): Promise<TeamSecret[]> {
  const { items } = await getJSON<{ items: TeamSecret[] }>(
    `${BASE}/${encodeURIComponent(teamId)}/secrets`,
  );
  return items;
}

/** setTeamSecret creates or rotates a team secret (maintainer+). Name must be a valid env var. */
export async function setTeamSecret(teamId: string, name: string, value: string): Promise<void> {
  await sendJSON(`${BASE}/${encodeURIComponent(teamId)}/secrets`, "POST", { name, value });
}

/** deleteTeamSecret removes one team secret by name (maintainer+). */
export async function deleteTeamSecret(teamId: string, name: string): Promise<void> {
  await request(
    `${BASE}/${encodeURIComponent(teamId)}/secrets/${encodeURIComponent(name)}`,
    { method: "DELETE" },
  );
}
